/**
 * reject-referral-dialog.tsx — Dialog to reject a referral with a reason.
 *
 * Submit button disabled until reason is non-empty.
 * Calls POST /admin/referrals/:id/reject with { reason }.
 * On success: calls onSuccess().
 * On error: shows error message; dialog stays open.
 *
 * PHI constraint: reason textarea does not log its value (safeLogger never
 * sees free-text coordinator input).
 */

import { useState, type JSX } from 'react';

export interface RejectReferralDialogProps {
  onReject: (reason: string) => Promise<void>;
  onClose: () => void;
  onSuccess: () => void;
}

export function RejectReferralDialog({
  onReject,
  onClose,
  onSuccess,
}: RejectReferralDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = reason.trim().length > 0 && !isPending;

  async function handleReject(): Promise<void> {
    if (!canSubmit) return;
    setIsPending(true);
    setError(null);
    try {
      await onReject(reason.trim());
      setIsPending(false);
      onSuccess();
    } catch (err) {
      setIsPending(false);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    }
  }

  return (
    <div role="dialog" aria-modal="true" className="space-y-4">
      <p className="text-sm text-gray-700">
        Enter a reason for rejecting this referral. The referring provider will be notified.
      </p>

      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Enter rejection reason…"
        rows={4}
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
      />

      {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isPending}
          className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleReject()}
          disabled={!canSubmit}
          className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
        >
          {isPending ? 'Rejecting…' : 'Reject Referral'}
        </button>
      </div>
    </div>
  );
}
